import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-date-picker';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import type { WorkplaceReservation, WorkplaceRetirementFailure } from '../services/floorplan-editor.service';
import { useRetirementImpact } from '../hooks/use-retirement-impact';
import datePickerClass from './workplace-reservations-dialog.styles';

/** `yyyy-MM-dd`, the shape both the endpoint and the impact hook expect. */
const asQueryDate = (date: Date): string => moment(date).format('YYYY-MM-DD');

/** A `yyyy-MM-dd` string as a local Date, for the picker. */
const asDate = (value: string): Date => moment(value, 'YYYY-MM-DD').toDate();

interface Props {
  readonly open: boolean;
  /** The `Workplaces.Id` behind the selected desk. */
  readonly workplaceId?: string;
  /** Earliest date the server will accept, `yyyy-MM-dd`, resolved in the office's timezone. */
  readonly earliestRetirementDate: string;
  /** The office's fill-in period, for stating the rule in words. */
  readonly planPeriodDays: number;
  readonly saving: boolean;
  readonly error?: WorkplaceRetirementFailure;
  readonly onConfirm: (retiresOn: string, allowInsideBookingWindow: boolean) => void;
  readonly onClose: () => void;
}

/**
 * Schedules a desk's withdrawal from service, and says who it would affect.
 *
 * Replaces plain deletion for any desk that has a `Workplaces` row behind it, because deleting
 * such a desk never actually removed it: the drawing and the bookable row are separate things,
 * and nothing in the booking path reads the drawing. A "deleted" desk stayed bookable and its
 * QR sticker went on scanning. Retirement is the honest version of that action — unbookable
 * from a date, nothing deleted, and every booking before the date left to go ahead.
 *
 * Three things here are deliberate and worth not undoing:
 *
 * - the calendar's floor comes from the server, not from the device clock, because the boundary
 *   depends on the office's booking timezone;
 * - the warning is built on an **uncapped** count, and says out loud when the list beneath it is
 *   shorter than that count. A list that silently truncates is the failure this dialog exists to
 *   prevent;
 * - it never claims a booking was made by an administrator. Nothing records who created one.
 */
const RetireDeskDialog = ({
  open,
  workplaceId,
  earliestRetirementDate,
  planPeriodDays,
  saving,
  error,
  onConfirm,
  onClose,
}: Props): React.JSX.Element => {
  const { t, i18n } = useTranslation();
  const [retiresOn, setRetiresOn] = useState<string | undefined>();
  const [acknowledged, setAcknowledged] = useState(false);
  const [allowInsideWindow, setAllowInsideWindow] = useState(false);

  // Opens on the earliest allowed date, with the override off. Most desks have nothing beyond it,
  // so the default is also the cheapest choice — and it is the only date guaranteed to be
  // offerable without disrupting a booking somebody has already made.
  useEffect(() => {
    if (open) {
      setRetiresOn(earliestRetirementDate || undefined);
      setAcknowledged(false);
      setAllowInsideWindow(false);
    }
  }, [open, earliestRetirementDate, workplaceId]);

  /**
   * Today at the office, derived from the server's own boundary rather than from the device clock.
   *
   * `earliestRetirementDate` is today + `planPeriodDays` in the office's booking timezone, so
   * subtracting the period gives back the office's today. A browser in another timezone reading
   * its own clock would offer a day the server then refuses, or refuse one it would have taken.
   */
  const officeToday = useMemo(() => {
    if (!earliestRetirementDate) return undefined;

    return moment(earliestRetirementDate, 'YYYY-MM-DD').subtract(planPeriodDays, 'days').toDate();
  }, [earliestRetirementDate, planPeriodDays]);

  // Re-consent whenever the date moves: an acknowledgement carried over from another date is
  // consent to something the administrator was never shown.
  useEffect(() => setAcknowledged(false), [retiresOn]);

  /**
   * Unticking the override pulls an inside-window date back to the safe boundary.
   *
   * Otherwise the calendar would keep showing a date the server is now going to refuse, and the
   * administrator would find out by pressing the button.
   */
  useEffect(() => {
    if (allowInsideWindow || !earliestRetirementDate) return;

    setRetiresOn((current) => (current && current < earliestRetirementDate ? earliestRetirementDate : current));
  }, [allowInsideWindow, earliestRetirementDate]);

  const impact = useRetirementImpact(workplaceId, retiresOn, open);

  const formatDate = (isoDate: string): string => new Date(isoDate).toLocaleDateString(i18n.language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const nameOf = (reservation: WorkplaceReservation): string => {
    const name = [reservation.firstName, reservation.lastName].filter(Boolean).join(' ').trim();

    return name || String(t('floorplan-editor-reservations-unknown-user'));
  };

  const affected = impact.data?.total ?? 0;
  const hasImpact = affected > 0;

  /**
   * Whether confirming is allowed.
   *
   * The button stays enabled when nothing is affected — the requirement explicitly permits
   * going ahead regardless — but an affected booking has to be acknowledged first, so the
   * warning cannot be clicked straight through. While the check is still running, or if it
   * failed, confirming is held back: proceeding on an unknown impact is the one case where the
   * administrator would be acting on information the dialog never managed to give them.
   */
  const confirmable = !!retiresOn
    && !saving
    && !impact.loading
    && !impact.error
    && (!hasImpact || acknowledged);

  const earliest = useMemo(
    () => (earliestRetirementDate ? asDate(earliestRetirementDate) : undefined),
    [earliestRetirementDate],
  );

  /** Whether the chosen date is one the server would refuse without the override. */
  const insideWindow = !!retiresOn && !!earliestRetirementDate && retiresOn < earliestRetirementDate;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      // The calendar is positioned next to its input, so any scroll box between it and the
      // viewport clips it — which is why the date row lives in the static header below rather
      // than in the scrolling content, and why the Paper does not clip. The Paper is still bound
      // by its own max height, so this lets the calendar overhang without letting the dialog
      // grow past the viewport.
      PaperProps={{ sx: { overflowY: 'visible' } }}
    >
      <DialogTitle>{t('floorplan-editor-retire-title')}</DialogTitle>

      {/* Static header: never scrolls, so the calendar it contains is never inside a scroll box.
          Everything that can grow without bound - the alerts, the table of affected bookings, the
          acknowledgement - is in the DialogContent below, which scrolls. Before this split the
          whole dialog grew with the list and pushed its own buttons off the bottom of the
          screen. */}
      <Box sx={{ px: 3, pb: 1, overflow: 'visible', flex: '0 0 auto' }}>
        <Stack spacing={2}>
          <DialogContentText variant="body2">
            {t('floorplan-editor-retire-explanation')}
          </DialogContentText>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
              {t('floorplan-editor-retire-date')}
            </Typography>
            <DatePicker
              format="dd/MM/yyyy"
              clearIcon={null}
              // The floor the server enforces, unless the override below is ticked — in which
              // case it drops to today at the office, which is as far back as the server will
              // ever go. Greying earlier days is not enough on its own, which is why the rule is
              // also written out below.
              minDate={allowInsideWindow ? officeToday : earliest}
              value={retiresOn ? asDate(retiresOn) : null}
              onChange={(value): void => {
                if (value instanceof Date) setRetiresOn(asQueryDate(value));
              }}
              className={datePickerClass}
            />
          </Stack>

          {/* Stated in words, not implied by a greyed-out calendar. An administrator who cannot
              click last Tuesday deserves to know why, and "because people can still book it"
              is the reason. */}
          {!!earliestRetirementDate && (
            <Typography variant="body2" color={StyleConstants.GREY_COLOR}>
              {t('floorplan-editor-retire-rule', {
                days: planPeriodDays,
                date: formatDate(earliestRetirementDate),
              })}
            </Typography>
          )}

          {/* Off by default, so the safe date is what an administrator gets without deciding
              anything. Ticking it is a different action from retiring a desk: inside the fill-in
              period the bookings that have to give way already exist, so this is the only route
              by which scheduling a retirement disrupts a plan somebody has already made rather
              than merely preventing a new one. The server enforces the same default. */}
          {!!earliestRetirementDate && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={allowInsideWindow}
                  onChange={(event): void => setAllowInsideWindow(event.target.checked)}
                />
              )}
              label={(
                <Typography variant="body2">
                  {t('floorplan-editor-retire-allow-inside-window', { days: planPeriodDays })}
                </Typography>
              )}
            />
          )}

        </Stack>
      </Box>

      {/* dividers gives the scroll box a visible top and bottom edge, so a list that continues
          below the fold reads as "there is more" rather than as "that is all of it". */}
      <DialogContent dividers sx={{ overflowY: 'auto' }}>
        <Stack spacing={2}>
          {insideWindow && !!retiresOn && (
            <Alert severity="warning">
              {t('floorplan-editor-retire-inside-window-warning', {
                date: formatDate(retiresOn),
              })}
            </Alert>
          )}

          {impact.loading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2">{t('floorplan-editor-retire-checking')}</Typography>
            </Stack>
          )}

          {impact.error === 'forbidden' && (
            <Alert severity="warning">{t('floorplan-editor-reservations-forbidden')}</Alert>
          )}

          {impact.error && impact.error !== 'forbidden' && (
            <Alert severity="error">{t('floorplan-editor-retire-check-failed')}</Alert>
          )}

          {/* The common case, and it should read as reassurance rather than as an absence. */}
          {!impact.loading && !impact.error && impact.data && !hasImpact && !!retiresOn && (
            <Alert severity="success">
              {t('floorplan-editor-retire-nobody-affected', { date: formatDate(retiresOn) })}
            </Alert>
          )}

          {!impact.loading && !impact.error && impact.data && hasImpact && !!retiresOn && (
            <>
              <Alert severity="warning">
                <Stack spacing={0.5}>
                  <span>
                    {t('floorplan-editor-retire-affected', {
                      count: affected,
                      date: formatDate(retiresOn),
                    })}
                  </span>
                  {/* Said explicitly, because the number above comes from an uncapped count and
                      the table below does not. Leaving it unsaid would present a year's worth of
                      rows as the whole picture. */}
                  {impact.data.truncated && (
                    <span>
                      {t('floorplan-editor-retire-showing-subset', {
                        shown: impact.data.reservations.length,
                        total: affected,
                      })}
                    </span>
                  )}
                </Stack>
              </Alert>

              <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                {t('floorplan-editor-retire-what-happens')}
              </Typography>

              {/* The table has no height cap of its own: the content area around it already
                  scrolls, and a scroll box inside a scroll box means two scrollbars and a list
                  that can be scrolled to a position where neither end is visible. */}
              {!!impact.data.reservations.length && (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('floorplan-editor-reservations-date')}</TableCell>
                        <TableCell>{t('name')}</TableCell>
                        <TableCell align="center">{t('morning')}</TableCell>
                        <TableCell align="center">{t('afternoon')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {impact.data.reservations.map((reservation) => (
                        <TableRow key={`${reservation.date}-${reservation.email ?? 'unknown'}`}>
                          <TableCell>{formatDate(reservation.date)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{nameOf(reservation)}</span>
                              {reservation.source === 'GroupReservation' && (
                                <Chip
                                  size="small"
                                  label={String(t('floorplan-editor-reservations-source-group'))}
                                  title={String(t('floorplan-editor-reservations-source-group-explanation'))}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            {reservation.morning ? t('floorplan-editor-reservations-booked') : '—'}
                          </TableCell>
                          <TableCell align="center">
                            {reservation.afternoon ? t('floorplan-editor-reservations-booked') : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <FormControlLabel
                control={(
                  <Checkbox
                    checked={acknowledged}
                    onChange={(event): void => setAcknowledged(event.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="body2">
                    {t('floorplan-editor-retire-acknowledge', { count: affected })}
                  </Typography>
                )}
              />
            </>
          )}

          {error === 'insideBookingWindow' && (
            <Alert severity="error">
              {t('floorplan-editor-retire-rule', {
                days: planPeriodDays,
                date: formatDate(earliestRetirementDate),
              })}
            </Alert>
          )}

          {error === 'dateInThePast' && (
            <Alert severity="error">{t('floorplan-editor-retire-date-in-the-past')}</Alert>
          )}

          {error === 'alreadyApplied' && (
            <Alert severity="error">{t('floorplan-editor-retire-already-applied')}</Alert>
          )}

          {error === 'forbidden' && (
            <Alert severity="error">{t('floorplan-editor-retire-forbidden')}</Alert>
          )}

          {(error === 'failed' || error === 'notFound') && (
            <Alert severity="error">{t('floorplan-editor-retire-failed')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('cancel')}</Button>
        <Button
          onClick={(): void => {
            if (retiresOn) onConfirm(retiresOn, allowInsideWindow);
          }}
          color="error"
          variant="contained"
          disabled={!confirmable}
        >
          {t('floorplan-editor-retire-confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetireDeskDialog;
